import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import haukeApiService from '../services/haukeApi';
import Uebersicht from '../components/profil-admin/Uebersicht';

import UeberUns from '../components/profil-admin/UeberUns';
import Online from '../components/profil-admin/Online';
import Dienste from '../components/profil-admin/Dienste';
import Zertifizierungen from '../components/profil-admin/Zertifizierungen';
import Bilder from '../components/profil-admin/Bilder';
import Oeffnungszeiten from '../components/profil-admin/Oeffnungszeiten';
import Produkte from '../components/profil-admin/Produkte';
import Notizen from '../components/profil-admin/Notizen';
import '../assets/css/profil-admin.css';

const ProfilAdmin = () => {
  const { hofId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [direkterzeuger, setDirekterzeuger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Übersicht', icon: 'dashboard' },

    { id: 'online', label: 'Online', icon: 'public' },
    { id: 'about', label: 'Über uns', icon: 'info' },
    { id: 'services', label: 'Dienste', icon: 'build' },
    { id: 'certifications', label: 'Zertifizierungen', icon: 'verified' },
    { id: 'hours', label: 'Öffnungszeiten', icon: 'schedule' },
    { id: 'gallery', label: 'Bilder', icon: 'photo_library' },
    { id: 'products', label: 'Produkte', icon: 'inventory' },
    { id: 'notes', label: 'Notizen', icon: 'sticky_note_2' },
    { id: 'ratings', label: 'Bewertungen', icon: 'star' }
  ];

  useEffect(() => {
    if (hofId) {
      // Immer nach oben scrollen beim Laden einer neuen HofId
      window.scrollTo(0, 0);
      loadDirekterzeugerDetails();
    }
  }, [hofId]);

  // Tab aus URL-Parameter setzen
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const loadDirekterzeugerDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading direkterzeuger details for hofId:', hofId);
      
      const response = await haukeApiService.getDirekterzeugerDetails(hofId);
      
      if (response.success) {
        setDirekterzeuger(response.data);
        console.log('Loaded direkterzeuger:', response.data);
      } else {
        setError('Direkterzeuger nicht gefunden');
      }
    } catch (error) {
      console.error('Error loading direkterzeuger:', error);
      setError(`Fehler beim Laden der Direkterzeuger-Details: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/direkterzeuger-admin');
  };

  const renderTabContent = () => {
    if (!direkterzeuger) return null;

    switch (activeTab) {
      case 'overview':
        return <Uebersicht hofId={hofId} />;

      case 'online':
        return <Online hofId={hofId} />;
      case 'about':
        return <UeberUns hofId={hofId} />;
      case 'services':
        return <Dienste hofId={hofId} />;
      case 'certifications':
        return <Zertifizierungen hofId={hofId} />;
      case 'hours':
        return <Oeffnungszeiten hofId={hofId} />;
      case 'gallery':
        return <Bilder hofId={hofId} />;
      case 'products':
        return <Produkte hofId={hofId} />;
      case 'notes':
        return <Notizen hofId={hofId} />;
      case 'ratings':
        return <RatingsTab data={direkterzeuger} />;
      default:
        return <Uebersicht hofId={hofId} />;
    }
  };

  if (loading) {
    return (
      <div className="profil-admin">
        <div className="loading-state">
          <i className="material-icons">hourglass_empty</i>
          <p>Lade Direkterzeuger-Details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profil-admin">
        <div className="error-state">
          <i className="material-icons">error</i>
          <p>{error}</p>
          <button className="btn btn-outline" onClick={handleGoBack}>
            <i className="material-icons">arrow_back</i>
            Zurück zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="profil-admin">
      {/* Header */}
      <div className="profil-header">
        <div className="header-content">
          <button className="back-button" onClick={handleGoBack}>
            <i className="material-icons">arrow_back</i>
            Zurück zur Übersicht
          </button>
          <div className="header-info">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h1>{direkterzeuger?.name || 'Direkterzeuger'}</h1>
              {direkterzeuger?.name && direkterzeuger?.city && (
                <button
                  className="google-search-btn"
                  onClick={() => {
                    const searchQuery = `${direkterzeuger.name} ${direkterzeuger.city}`;
                    window.open(`https://google.de/search?q=${encodeURIComponent(searchQuery)}`, '_blank');
                  }}
                  title={`Suche "${direkterzeuger.name} ${direkterzeuger.city}" bei Google`}
                >
                  <i className="material-icons">search</i>
                </button>
              )}
            </div>
            <div className="header-meta">
              <span className="hof-id">HofId: {hofId}</span>
              <span className="location">
                <i className="material-icons">location_on</i>
                {direkterzeuger?.city}, {direkterzeuger?.plz}
              </span>
              {direkterzeuger?.membership && (
                <span className={`membership-badge ${direkterzeuger.membership}`}>
                  {direkterzeuger.membership}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <div className="tab-container">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className="material-icons">{tab.icon}</i>
              <span>{tab.label}</span>
              {direkterzeuger?.subcollectionCounts?.[tab.id] > 0 && (
                <span className="tab-count">
                  {direkterzeuger.subcollectionCounts[tab.id]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

// Online Tab Component
const OnlineTab = ({ data }) => (
  <div className="tab-panel">
    <div className="section-card">
      <div className="card-header">
        <h3>Online-Präsenz</h3>
        <i className="material-icons">public</i>
      </div>
      <div className="card-content">
        <div className="info-grid">
          <div className="info-item">
            <label>Website</label>
            <span>
              {data.website ? (
                <a href={data.website} target="_blank" rel="noopener noreferrer">
                  {data.website}
                </a>
              ) : 'Nicht verfügbar'}
            </span>
          </div>
          <div className="info-item">
            <label>E-Mail öffentlich</label>
            <span>{data.mail_public ? 'Ja' : 'Nein'}</span>
          </div>
          <div className="info-item">
            <label>Google Maps</label>
            <span>
              {data.latitude && data.longitude ? (
                <a 
                  href={`https://maps.google.com/?q=${data.latitude},${data.longitude}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Karte öffnen
                </a>
              ) : 'Nicht verfügbar'}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// About Tab Component
const AboutTab = ({ data }) => (
  <div className="tab-panel">
    <div className="section-card">
      <div className="card-header">
        <h3>Über uns</h3>
        <i className="material-icons">info</i>
      </div>
      <div className="card-content">
        <div className="description-content">
          {data.description ? (
            <p>{data.description}</p>
          ) : (
            <p className="no-content">Keine Beschreibung verfügbar</p>
          )}
        </div>
        {data.categories && data.categories.length > 0 && (
          <div className="categories-section">
            <h4>Kategorien</h4>
            <div className="categories-list">
              {data.categories.map((category, index) => (
                <span key={index} className="category-tag">
                  {category}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

// Services Tab Component
const ServicesTab = ({ data }) => (
  <div className="tab-panel">
    <div className="section-card">
      <div className="card-header">
        <h3>Dienste</h3>
        <i className="material-icons">build</i>
      </div>
      <div className="card-content">
        {data.subcollections?.services?.length > 0 ? (
          <div className="services-list">
            {data.subcollections.services.map((service, index) => (
              <div key={index} className="service-item">
                <h4>{service.name}</h4>
                <p>{service.description}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-content">Keine Dienste verfügbar</p>
        )}
      </div>
    </div>
  </div>
);

// Certifications Tab Component
const CertificationsTab = ({ data }) => (
  <div className="tab-panel">
    <div className="section-card">
      <div className="card-header">
        <h3>Zertifizierungen</h3>
        <i className="material-icons">verified</i>
      </div>
      <div className="card-content">
        {data.subcollections?.certifications?.length > 0 ? (
          <div className="certifications-list">
            {data.subcollections.certifications.map((cert, index) => (
              <div key={index} className="certification-item">
                <h4>{cert.name}</h4>
                <p>{cert.description}</p>
                {cert.validUntil && (
                  <span className="valid-until">Gültig bis: {cert.validUntil}</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="no-content">Keine Zertifizierungen verfügbar</p>
        )}
      </div>
    </div>
  </div>
);

// Ratings Tab Component
const RatingsTab = ({ data }) => (
  <div className="tab-panel">
    <div className="section-card">
      <div className="card-header">
        <h3>Bewertungen</h3>
        <i className="material-icons">star</i>
      </div>
      <div className="card-content">
        {data.subcollections?.ratings?.length > 0 ? (
          <div className="ratings-list">
            {data.subcollections.ratings.map((rating, index) => (
              <div key={index} className="rating-item">
                <div className="rating-header">
                  <span className="rating-stars">
                    {'★'.repeat(rating.stars)}{'☆'.repeat(5 - rating.stars)}
                  </span>
                  <span className="rating-date">{rating.date}</span>
                </div>
                <p>{rating.comment}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-content">Keine Bewertungen verfügbar</p>
        )}
      </div>
    </div>
  </div>
);

export default ProfilAdmin; 